import { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isFailed, isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { useAsyncFetcher } from 'reporting-data/asyncData/useAsyncFetcher';
import { DEFAULT_FISCAL_YEAR_MONTH_NAME } from 'reporting-data/constants/dates';
// @ts-expect-error Untyped import
import ReportLoading from '../../reports/inferLogic/components/states/ReportLoading';
import { HubsSettingClient } from 'frontend-preferences-client';
import { isConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { getUseFiscalYear, getUseFiscalYearInAggregation } from 'reporting-data/lib/fiscalYearUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const FiscalYearContext = /*#__PURE__*/createContext(undefined);
const hubsSettingClient = HubsSettingClient.forCaller('reporting-ui-components');
export const fiscalYearFetcher = () => {
  return hubsSettingClient.fetch('hubspot:fiscalyear', DEFAULT_FISCAL_YEAR_MONTH_NAME);
};
const fiscalYearResultFormatter = result => {
  return result;
};
const FiscalYearProvider = ({
  fiscalYearMonthName: passedInFiscalYearMonth,
  report,
  children
}) => {
  const isFiscalYearReport = report && isConfigReport(report) && (getUseFiscalYear(report.get('config')) || getUseFiscalYearInAggregation(report.get('config')));
  const deferred = (report ? !isFiscalYearReport : true) || passedInFiscalYearMonth !== undefined;
  const asyncFiscalYearData = useAsyncFetcher({
    fetcher: fiscalYearFetcher,
    resultFormatter: fiscalYearResultFormatter,
    deferred
  });
  const fiscalYearMonthName = useMemo(() => {
    if (passedInFiscalYearMonth) {
      return passedInFiscalYearMonth;
    }
    if (isFailed(asyncFiscalYearData) || deferred) {
      return DEFAULT_FISCAL_YEAR_MONTH_NAME;
    }
    if (isLoading(asyncFiscalYearData)) {
      return undefined;
    }
    if (isSucceeded(asyncFiscalYearData)) {
      return asyncFiscalYearData.data;
    }
    return undefined;
  }, [asyncFiscalYearData, deferred, passedInFiscalYearMonth]);
  if (!fiscalYearMonthName) {
    return /*#__PURE__*/_jsx(ReportLoading, {
      "data-test-id": "fiscal-year-provider-loading"
    });
  }
  return /*#__PURE__*/_jsx(FiscalYearContext.Provider, {
    value: fiscalYearMonthName,
    children: children
  });
};
FiscalYearProvider.displayName = 'FiscalYearProvider';
FiscalYearProvider.propTypes = {
  children: PropTypes.node.isRequired,
  report: PropTypes.object
};
export default FiscalYearProvider;
export const WithFiscalYearProvider = Component => {
  return function WithFiscalYearContext(props) {
    const fiscalYearFromContext = useContext(FiscalYearContext);
    const fiscalYearFromProps = props && props.fiscalYearMonthName;
    const fiscalYearMonthName = fiscalYearFromProps || fiscalYearFromContext;
    const report = props && props.report;
    return /*#__PURE__*/_jsx(FiscalYearProvider, {
      fiscalYearMonthName: fiscalYearMonthName,
      report: report,
      children: /*#__PURE__*/_jsx(Component, Object.assign({}, props))
    });
  };
};